/**
 * ContactDetailModal — view / edit / create for Contacts and Organizations.
 *
 * Props:
 *   mode       — 'view' | 'edit' | 'create'
 *   kind       — 'contact' | 'org'
 *   record     — existing Airtable record (null in create mode)
 *   contacts   — array of Contact records (for org primary-contact picker)
 *   orgs       — array of Organization records (for contact org picker)
 *   typePill   — style function from ContactsPanel
 *   onEdit()   — switch to edit mode
 *   onClose()  — close modal
 *   onSave(fields) — async; throws on error
 *   onDelete(id) — async; throws on error
 */

import { useState, useEffect } from 'react'
import Select from './Select.jsx'
import { getContactNamesByIds, getOrgNamesByIds, updateContact, getContactOrigin } from '../services/contacts.js'
import { isAlderJamesOrg } from '../services/team.js'
import { REPRO_ORG_TYPE, getShopCities, setShopCities, parseCities, COUNTY_COVERAGE_ORG_TYPES, getProviderCoverage, setProviderCoverage } from '../services/printOrders.js'
import ContactPicker from './crm/ContactPicker.jsx'
import OrganizationPicker from './crm/OrganizationPicker.jsx'
import { splitAddress, cityProblem } from '../lib/addressSplit.js'

/* Pasting a whole address into Street or City fills all four boxes instead (lib/addressSplit.js). */
function onAddressPaste(setStreet, setCity, setState, setZip) {
  return e => {
    const parts = splitAddress(e.clipboardData && e.clipboardData.getData('text'))
    if (!parts) return
    e.preventDefault()
    setStreet(parts.street)
    setCity(parts.city)
    setState(parts.state)
    if (parts.zip) setZip(parts.zip)
  }
}

const CONTACT_TYPES = [
  'Active Client','Active Prospect','Active Lead','Inactive Lead',
  'Team','Internal Consultant','External Consultant',
  'General Contractor','Construction Manager','Owner Representative',
  'Subcontractor','Vendor','Agency','HOA','Past Client','Former Team',
  'Personal','Official','Real Estate Agent','Project Stakeholder','Referral',
]

const ORG_TYPES = [
  'Agency','As-Built','Civil Engineer','Client','Electrical Engineer','Energy Consultant',
  'General Contractor','Geotechnical Engineer','HOA','Interior Designer',
  'Landscape Architect','Manufacturer','Mechanical Engineer','Owner',
  'Prospect','Reprographics','Structural Engineer','Surveyor','Vendor',
]

const AJ_ROLES = ['Principal Architect','Architect','Designer','Principal','Administrative']

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','District of Columbia','Florida','Georgia','Hawaii','Idaho','Illinois',
  'Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts',
  'Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada',
  'New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota',
  'Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina',
  'South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington',
  'West Virginia','Wisconsin','Wyoming',
]

const ORIGIN_VIA = {
  contacts: 'in Contacts',
  lead: 'from a lead',
  lead_conversion: 'by converting a lead',
  parcel_research: 'in Parcel Research',
}

function originLine(o) {
  if (!o) return 'Who added this was not recorded (added before Cortex kept track).'
  const d = o.at ? new Date(o.at) : null
  const when = d && !isNaN(d) ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''
  return `Added by ${o.by}${when ? ' on ' + when : ''} ${ORIGIN_VIA[o.via] || ''}`.trim()
}

function contactLabel(c) {
  const cf = (c && c.fields) || {}
  return cf['Name'] || [cf['First Name'], cf['Last Name']].filter(Boolean).join(' ') || (c && c.id) || ''
}

function orgLabel(o) {
  const of = (o && o.fields) || {}
  return of['Organization Name'] || (o && o.id) || ''
}

function formatAddress(street, city, state, zip) {
  const cityLine = [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')
  return [street, cityLine].filter(Boolean).join(', ') || null
}

// Themed via the app's existing CSS custom properties (outline.css :root / body.dark) — these
// already flip automatically with the theme, so no useDarkMode() wiring is needed in this file.
const S = {
  label: {
    display: 'block',
    fontFamily: '"DM Mono",monospace',
    fontSize: 10,
    color: 'var(--aj-text-muted)',
    letterSpacing: '0.04em',
    marginBottom: 4,
    textTransform: 'uppercase',
  },
  input: {
    width: '100%',
    padding: '7px 10px',
    fontFamily: '"DM Sans",sans-serif',
    fontSize: 12,
    color: 'var(--aj-text)',
    background: 'var(--aj-bg-subtle)',
    border: '0.5px solid var(--aj-border)',
    borderRadius: 4,
    boxSizing: 'border-box',
    outline: 'none',
  },
  value: {
    fontFamily: '"DM Sans",sans-serif',
    fontSize: 12,
    color: 'var(--aj-text)',
    minHeight: 20,
  },
}

function Field({ label, children }) {
  return (
    <div>
      <label style={S.label}>{label}</label>
      {children}
    </div>
  )
}

function ReadValue({ value }) {
  if (!value) return <span style={{ ...S.value, color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>—</span>
  return <span style={S.value}>{value}</span>
}


export default function ContactDetailModal({
  mode, kind, record, contacts, orgs, typePill,
  onEdit, onClose, onSave, onDelete,
  prefill = null, // create-mode defaults, e.g. { 'Contact Type': 'Team' }
  linkedOrgIds = [], // contact's current org (derived from org side, since the
                     // contact list doesn't fetch its own link field)
}) {
  const f = record?.fields || prefill || {}
  const isContact = kind === 'contact'
  const isEdit    = mode === 'edit' || mode === 'create'
  const isCreate  = mode === 'create'

  // Contact fields
  const [firstName,  setFirstName]  = useState(f['First Name']  || '')
  const [lastName,   setLastName]   = useState(f['Last Name']   || '')
  const [contactType,setContactType]= useState(f['Contact Type'] || '')
  const [email,      setEmail]      = useState(f['Email']       || '')
  const [phone,      setPhone]      = useState(f['Phone']       || '')
  const [jobTitle,   setJobTitle]   = useState(f['Job Title']   || '')
  const [ajRole,     setAjRole]     = useState(f['Alder James Role'] || '')
  const [notes,      setNotes]      = useState(f['Contact Notes'] || '')
  const [street,     setStreet]     = useState(f['Street Address'] || '')
  const [city,       setCity]       = useState(f['City'] || '')
  const [contState,  setContState]  = useState(f['State'] || '')
  const [zip,        setZip]        = useState(f['Zip'] || '')
  // Organizations link (array of IDs) — seeded from the parent-derived
  // linkedOrgIds (contact records don't carry their own org link in the list)
  const [orgIds,     setOrgIds]     = useState(
    (linkedOrgIds && linkedOrgIds.length) ? linkedOrgIds.slice(0, 1) : (f['Organization Contacts'] || []).slice(0, 1)
  )
  // Name of the linked org — resolved by ID so it shows even when the org is beyond the first
  // browse page (same class of bug as primaryName below: an org sorted past the loaded page
  // looked blank/unfindable here).
  const [orgLinkName, setOrgLinkName] = useState('')

  // Organization fields
  const [orgName,    setOrgName]    = useState(f['Organization Name'] || '')
  const [orgType,    setOrgType]    = useState(Array.isArray(f['Type']) ? (f['Type'][0] || '') : (f['Type'] || ''))
  const [orgPhone,   setOrgPhone]   = useState(f['Phone'] || '')
  const [orgEmail,   setOrgEmail]   = useState(f['General Email'] || '')
  const [orgWebsite, setOrgWebsite] = useState(f['Website'] || '')
  const [orgAddress, setOrgAddress] = useState(f['Client Address'] || '')
  const [orgCity,    setOrgCity]    = useState(f['Client City']    || '')
  const [orgState,   setOrgState]   = useState(f['Client State']   || '')
  const [orgZip,     setOrgZip]     = useState(f['Client Zip']     || '')
  const [primaryId,  setPrimaryId]  = useState((f['Primary Contact'] || [])[0] || '')
  // Name of the selected primary contact — resolved by ID so it shows even when the contact is
  // beyond the first browse page (the bug: an org's primary set elsewhere looked blank here).
  const [primaryName, setPrimaryName] = useState('')

  // Org membership — the org's own "Contacts" link field (Airtable's inverse of the Contact's
  // "Organization Contacts" field; writing either side updates both). memberIds is the live/
  // authoritative id list; orgMembers is it resolved to display names for the list UI and for
  // restricting the Primary Contact picker to actual members (requested explicitly).
  const [memberIds,    setMemberIds]    = useState(f['Contacts'] || [])
  const [orgMembers,   setOrgMembers]   = useState([])   // [{id, name}]
  const [addingMember, setAddingMember] = useState(false)
  const [memberBusy,   setMemberBusy]   = useState(null) // contact id currently being removed

  // Print shops (Type Reprographics): the cities the shop serves — Supabase local_resource_cities,
  // saved alongside the Airtable record (docs/PRINT_ORDERS.md). null = not loaded yet.
  const [servedCities, setServedCities] = useState(null)
  const isPrintShop = !isContact && !isCreate && orgType === REPRO_ORG_TYPE
  useEffect(() => {
    if (!isPrintShop || !record || servedCities !== null) return
    getShopCities(record.id).then(c => setServedCities(c.join(', '))).catch(() => setServedCities(''))
  }, [isPrintShop, record && record.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // An organization can carry more than one Type (T&M is Surveyor + As-Built); the picker edits
  // the first, and the rest are kept on save rather than dropped.
  const extraTypes = (Array.isArray(f['Type']) ? f['Type'] : []).slice(1)
  // Surveyors and as-built firms: the COUNTIES they serve, or nationwide (database/391). Nothing
  // listed = offered on no project. null = not loaded yet.
  const [servedCounties, setServedCounties] = useState(null)
  const [nationwide,     setNationwide]     = useState(false)
  const isCountyProvider = !isContact && !isCreate && [orgType, ...extraTypes].some(t => COUNTY_COVERAGE_ORG_TYPES.includes(t))
  useEffect(() => {
    if (!isCountyProvider || !record || servedCounties !== null) return
    getProviderCoverage(record.id)
      .then(c => { setServedCounties(c.counties.join(', ')); setNationwide(c.nationwide) })
      .catch(() => setServedCounties(''))
  }, [isCountyProvider, record && record.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const [submitting, setSubmitting] = useState(false)
  const [error,      setError]      = useState(null)
  const [confirmDel, setConfirmDel] = useState(false)

  const displayName = isContact
    ? (f['Name'] || [f['First Name'], f['Last Name']].filter(Boolean).join(' ') || 'New Contact')
    : (f['Organization Name'] || 'Unnamed organization')

  // Who added this record and how (database/368). undefined = loading, null = not recorded.
  const [origin, setOrigin] = useState(undefined)
  useEffect(() => {
    if (isCreate || !record || !record.id) return
    let live = true
    getContactOrigin(record.id, isContact ? 'person' : 'org').then(o => { if (live) setOrigin(o) }).catch(() => { if (live) setOrigin(null) })
    return () => { live = false }
  }, [record && record.id]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSave() {
    const badCity = cityProblem(isContact ? city : orgCity)
    if (badCity) { setError(badCity); return }
    setSubmitting(true)
    setError(null)
    try {
      let fields
      if (isContact) {
        fields = {
          'First Name': firstName.trim(),
          'Last Name':  lastName.trim(),
          'Contact Type': contactType || null,
          'Email':      email.trim() || null,
          'Phone':      phone.trim() || null,
          'Job Title':  jobTitle.trim() || null,
          'Alder James Role': ajRole || null,
          'Contact Notes':   notes.trim() || null,
          'Street Address':  street.trim() || null,
          'City':            city.trim() || null,
          'State':           contState || null,
          'Zip':             zip.trim() || null,
          'Organization Contacts': orgIds.length ? orgIds : [],
        }
      } else {
        fields = {
          'Organization Name': orgName.trim(),
          // Type is a multipleSelects field — it must be an array, not a string, or Airtable 422s.
          'Type':         [...new Set([orgType, ...extraTypes].filter(Boolean))],
          'Phone':        orgPhone.trim() || null,
          'General Email':orgEmail.trim() || null,
          'Website':      orgWebsite.trim() || null,
          'Client Address': orgAddress.trim() || null,
          'Client City':    orgCity.trim() || null,
          'Client State':   orgState.trim() || null,
          'Client Zip':     orgZip.trim() || null,
          'Primary Contact': primaryId ? [primaryId] : [],
        }
      }
      if (isPrintShop && servedCities !== null) await setShopCities(record.id, parseCities(servedCities))
      if (isCountyProvider && servedCounties !== null) await setProviderCoverage(record.id, parseCities(servedCounties), nationwide)
      await onSave(fields)
    } catch (ex) {
      setError(ex.message || 'Failed to save')
      setSubmitting(false)
    }
  }

  async function handleDelete() {
    setSubmitting(true)
    setError(null)
    try {
      await onDelete(record.id)
    } catch (ex) {
      setError(ex.message || 'Failed to delete')
      setSubmitting(false)
      setConfirmDel(false)
    }
  }

  // Resolve the selected primary contact's name: prefer the locally-loaded record, else fetch by id
  // (covers contacts beyond the first browse page). Runs in view and edit for organizations.
  useEffect(() => {
    if (isContact) return
    if (!primaryId) { setPrimaryName(''); return }
    const local = (contacts || []).find(c => c.id === primaryId)
    if (local) { setPrimaryName(contactLabel(local)); return }
    let alive = true
    getContactNamesByIds([primaryId]).then(map => { if (alive) setPrimaryName(map[primaryId] || '') })
    return () => { alive = false }
  }, [primaryId, isContact, contacts])

  // Resolve the contact's linked org name the same way: prefer the locally-loaded record, else
  // fetch by id (covers orgs beyond the first browse page — the exact bug that made an org like
  // "Knapp" unfindable/blank when it sorted past the loaded page). Runs in view and edit for contacts.
  useEffect(() => {
    if (!isContact) return
    const orgId = orgIds[0]
    if (!orgId) { setOrgLinkName(''); return }
    const local = (orgs || []).find(o => o.id === orgId)
    if (local) { setOrgLinkName(orgLabel(local)); return }
    let alive = true
    getOrgNamesByIds([orgId]).then(map => { if (alive) setOrgLinkName(map[orgId] || '') })
    return () => { alive = false }
  }, [orgIds, isContact, orgs])

  // A "Team" contact with no Organization is exactly the silent gap that made new hires invisible
  // in Reference > Team > Alder James's "Add team members" picker (it reads the org's own Contacts
  // link, not Contact Type alone — see the 2026-07 Giovana Alvarenga fix). The auto-provisioning
  // path now sets this link on create, but a human can still pick "Team" in this generic modal and
  // simply forget to set an Organization, since it isn't required. Team is Alder James by
  // definition, so auto-fill it the moment Team is selected while no org is set yet — never
  // overwrites an org someone already picked.
  useEffect(() => {
    if (!isContact || !isEdit) return
    if (contactType !== 'Team' || orgIds.length) return
    const ajOrg = (orgs || []).find(isAlderJamesOrg)
    if (ajOrg) { setOrgIds([ajOrg.id]); setOrgLinkName(ajOrg.fields['Organization Name'] || '') }
  }, [contactType, orgs, isContact, isEdit, orgIds])

  // Resolve member names for the org's Contacts list. Always a batch fetch (rather than
  // local-cache-first) — a handful of names in one call is simpler than merging partial local
  // matches for a whole list.
  useEffect(() => {
    if (isContact || !memberIds.length) { setOrgMembers([]); return }
    let alive = true
    getContactNamesByIds(memberIds).then(map => {
      if (alive) setOrgMembers(memberIds.map(id => ({ id, name: map[id] || id })))
    })
    return () => { alive = false }
  }, [memberIds, isContact])

  // Add/remove write straight to the CONTACT record's own Organization Contacts field (the
  // authoritative single-org-per-contact link the Contact edit modal also manages) — Airtable's
  // bidirectional link then reflects it back onto this org's Contacts field automatically.
  async function handleAddMember(contactId, contactName) {
    if (!contactId || memberIds.includes(contactId)) { setAddingMember(false); return }
    setAddingMember(false)
    try {
      await updateContact(contactId, { 'Organization Contacts': [record.id] })
      setMemberIds(prev => [...prev, contactId])
    } catch (ex) {
      setError(ex.message || 'Failed to add contact')
    }
  }

  async function handleRemoveMember(contactId) {
    setMemberBusy(contactId)
    try {
      await updateContact(contactId, { 'Organization Contacts': [] })
      setMemberIds(prev => prev.filter(id => id !== contactId))
      if (primaryId === contactId) { setPrimaryId(''); setPrimaryName('') }
    } catch (ex) {
      setError(ex.message || 'Failed to remove contact')
    } finally {
      setMemberBusy(null)
    }
  }

  // Primary Contact can be ANY contact, not just an existing org member (2026-08 — a hard
  // "must already be a member" restriction turned out to just lock people out of picking the
  // primary contact they actually needed). Picking someone not yet in this org's Contacts list
  // doesn't block the save; ContactsLauncher's handleSave links them into Contacts automatically
  // right after saving (see `syncPrimaryContactMembership`) — the inline note below just tells the
  // user that's about to happen.
  const primaryNeedsMembership = !!primaryId && !memberIds.includes(primaryId)

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 900,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div data-modal-id="ContactDetailModal" style={{
        background: 'var(--aj-bg-elevated)',
        borderRadius: 8,
        boxShadow: '0 12px 48px rgba(0,0,0,0.35)',
        width: 520,
        maxHeight: 'calc(100vh - 80px)',
        overflowY: 'auto',
        padding: '20px 20px 16px',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 15, fontWeight: 700, color: 'var(--aj-text)' }}>
              {isCreate ? (isContact ? 'New Contact' : 'New Organization') : displayName}
            </div>
            {(() => {
              const rawT = f['Contact Type'] || f['Type']
              const tStr = Array.isArray(rawT) ? (rawT[0] || '') : (rawT || '')
              if (isCreate || isEdit || !tStr) return null
              return <span style={{ ...typePill(tStr), marginTop: 4, display: 'inline-block' }}>{tStr}</span>
            })()}
            {!isEdit && origin !== undefined && (
              <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 6 }}>
                {originLine(origin)}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {!isEdit && (
              <button
                onClick={onEdit}
                style={{
                  padding: '4px 12px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600,
                  background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)',
                }}
              >Edit</button>
            )}
            {!isCreate && !isEdit && (
              <button
                onClick={() => setConfirmDel(true)}
                style={{ padding: '4px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: 'none', cursor: 'pointer', color: '#c0392b' }}
              >Delete</button>
            )}
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 18, lineHeight: 1, padding: 2 }}>×</button>
          </div>
        </div>

        {/* Delete confirmation */}
        {confirmDel && (
          <div style={{ background: 'rgba(192,57,43,0.08)', border: '0.5px solid rgba(192,57,43,0.3)', borderRadius: 6, padding: 12, marginBottom: 16 }}>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#c0392b', marginBottom: 10 }}>
              Delete this {isContact ? 'contact' : 'organization'}? This cannot be undone.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setConfirmDel(false)} style={{ padding: '4px 12px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
              <button onClick={handleDelete} disabled={submitting} style={{ padding: '4px 12px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: '#c0392b', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                {submitting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        )}

        {/* Fields */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {isContact ? (
            <>
              {/* Name row */}
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <Field label="First Name">
                    {isEdit
                      ? <input style={S.input} value={firstName} onChange={e => setFirstName(e.target.value)} autoFocus={isCreate} />
                      : <ReadValue value={f['First Name']} />}
                  </Field>
                </div>
                <div style={{ flex: 1 }}>
                  <Field label="Last Name">
                    {isEdit
                      ? <input style={S.input} value={lastName} onChange={e => setLastName(e.target.value)} />
                      : <ReadValue value={f['Last Name']} />}
                  </Field>
                </div>
              </div>

              <Field label="Contact Type">
                {isEdit
                  ? <Select value={contactType} onChange={setContactType}
                      options={[{ value: '', label: '— none —' }].concat(CONTACT_TYPES.map(t => ({ value: t, label: t })))}
                      searchable style={{ width: '100%', fontSize: 12 }} />
                  : <ReadValue value={f['Contact Type']} />}
              </Field>

              <Field label="Email">
                {isEdit
                  ? <input style={S.input} type="email" value={email} onChange={e => setEmail(e.target.value)} />
                  : f['Email'] ? <a href={'mailto:' + f['Email']} style={{ ...S.value, color: '#2563eb' }}>{f['Email']}</a> : <ReadValue value={null} />}
              </Field>

              <Field label="Phone">
                {isEdit
                  ? <input style={S.input} type="tel" value={phone} onChange={e => setPhone(e.target.value)} />
                  : f['Phone'] ? <a href={'tel:' + f['Phone']} style={{ ...S.value, color: '#2563eb' }}>{f['Phone']}</a> : <ReadValue value={null} />}
              </Field>

              <Field label="Job Title">
                {isEdit
                  ? <input style={S.input} value={jobTitle} onChange={e => setJobTitle(e.target.value)} />
                  : <ReadValue value={f['Job Title']} />}
              </Field>

              <Field label="Organization">
                {isEdit
                  ? <OrganizationPicker value={orgIds[0] || ''} name={orgLinkName} seed={orgs} inputStyle={S.input}
                      onChange={(id, nm) => { setOrgIds(id ? [id] : []); setOrgLinkName(nm) }} />
                  : <ReadValue value={orgLinkName || null} />}
              </Field>

              <Field label="Alder James Role">
                {isEdit
                  ? <Select value={ajRole} onChange={setAjRole}
                      options={[{ value: '', label: '— none —' }].concat(AJ_ROLES.map(r => ({ value: r, label: r })))}
                      searchable style={{ width: '100%', fontSize: 12 }} />
                  : <ReadValue value={f['Alder James Role']} />}
              </Field>

              <Field label="Address">
                {isEdit ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <input style={S.input} value={street} onChange={e => setStreet(e.target.value)} placeholder="Street address (or paste the full address)"
                      onPaste={onAddressPaste(setStreet, setCity, setContState, setZip)} />
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input style={{ ...S.input, flex: 2 }} value={city} onChange={e => setCity(e.target.value)} placeholder="City"
                        onPaste={onAddressPaste(setStreet, setCity, setContState, setZip)} />
                      <Select value={contState} onChange={setContState}
                        options={[{ value: '', label: '— none —' }].concat(US_STATES.map(s => ({ value: s, label: s })))}
                        placeholder="State" searchable style={{ flex: 1, fontSize: 12 }} />
                      <input style={{ ...S.input, flex: 1 }} value={zip} onChange={e => setZip(e.target.value)} placeholder="ZIP" />
                    </div>
                  </div>
                ) : (
                  <ReadValue value={formatAddress(f['Street Address'], f['City'], f['State'], f['Zip'])} />
                )}
              </Field>

              <Field label="Notes">
                {isEdit
                  ? <textarea style={{ ...S.input, resize: 'vertical', minHeight: 60 }} value={notes} onChange={e => setNotes(e.target.value)} />
                  : <ReadValue value={f['Contact Notes']} />}
              </Field>
            </>
          ) : (
            <>
              <Field label="Organization Name">
                {isEdit
                  ? <input style={S.input} value={orgName} onChange={e => setOrgName(e.target.value)} autoFocus={isCreate} />
                  : <ReadValue value={f['Organization Name']} />}
              </Field>

              <Field label="Type">
                {isEdit
                  ? <Select value={orgType} onChange={setOrgType}
                      options={[{ value: '', label: '— none —' }].concat(ORG_TYPES.map(t => ({ value: t, label: t })))}
                      searchable style={{ width: '100%', fontSize: 12 }} />
                  : <ReadValue value={Array.isArray(f['Type']) ? (f['Type'][0] || '') : f['Type']} />}
              </Field>

              <Field label="Primary Contact">
                {isEdit
                  ? <>
                      <ContactPicker value={primaryId} name={primaryName} seed={contacts} inputStyle={S.input}
                        onChange={(id, nm) => { setPrimaryId(id); setPrimaryName(nm) }} />
                      {primaryNeedsMembership && (
                        <div style={{ marginTop: 4, fontFamily: '"DM Sans",sans-serif', fontSize: 10.5, color: 'var(--aj-text-mid)' }}>
                          Not yet one of this org's contacts — will be added automatically when you save.
                        </div>
                      )}
                    </>
                  : <ReadValue value={primaryName || null} />}
              </Field>

              <Field label="Phone">
                {isEdit
                  ? <input style={S.input} type="tel" value={orgPhone} onChange={e => setOrgPhone(e.target.value)} />
                  : f['Phone'] ? <a href={'tel:' + f['Phone']} style={{ ...S.value, color: '#2563eb' }}>{f['Phone']}</a> : <ReadValue value={null} />}
              </Field>

              <Field label="General Email">
                {isEdit
                  ? <input style={S.input} type="email" value={orgEmail} onChange={e => setOrgEmail(e.target.value)} />
                  : f['General Email'] ? <a href={'mailto:' + f['General Email']} style={{ ...S.value, color: '#2563eb' }}>{f['General Email']}</a> : <ReadValue value={null} />}
              </Field>

              <Field label="Website">
                {isEdit
                  ? <input style={S.input} type="url" value={orgWebsite} onChange={e => setOrgWebsite(e.target.value)} placeholder="https://…" />
                  : f['Website'] ? <a href={f['Website']} target="_blank" rel="noopener noreferrer" style={{ ...S.value, color: '#2563eb' }}>{f['Website']}</a> : <ReadValue value={null} />}
              </Field>

              <Field label="Address">
                {isEdit ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <input style={S.input} value={orgAddress} onChange={e => setOrgAddress(e.target.value)} placeholder="Street address (or paste the full address)"
                      onPaste={onAddressPaste(setOrgAddress, setOrgCity, setOrgState, setOrgZip)} />
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input style={{ ...S.input, flex: 2 }} value={orgCity} onChange={e => setOrgCity(e.target.value)} placeholder="City"
                        onPaste={onAddressPaste(setOrgAddress, setOrgCity, setOrgState, setOrgZip)} />
                      <Select value={orgState} onChange={setOrgState}
                        options={[{ value: '', label: '— none —' }].concat(US_STATES.map(s => ({ value: s, label: s })))}
                        placeholder="State" searchable style={{ flex: 1, fontSize: 12 }} />
                      <input style={{ ...S.input, flex: 1 }} value={orgZip} onChange={e => setOrgZip(e.target.value)} placeholder="ZIP" />
                    </div>
                  </div>
                ) : (
                  <ReadValue value={formatAddress(f['Client Address'], f['Client City'], f['Client State'], f['Client Zip'])} />
                )}
              </Field>

              {isPrintShop && (
                <Field label="Cities served">
                  {isEdit
                    ? <>
                        <input style={S.input} value={servedCities || ''} onChange={e => setServedCities(e.target.value)}
                          placeholder={servedCities === null ? 'Loading…' : 'e.g. Laguna Beach, Laguna Niguel, Dana Point'} disabled={servedCities === null} />
                        <div style={{ fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 4, fontFamily: '"DM Sans",sans-serif' }}>
                          City names separated by commas. Projects there (and in the shop's own city) list this shop first under Team &gt; Local Resources. Empty = every project.
                        </div>
                      </>
                    : <ReadValue value={servedCities === null ? '…' : (servedCities || 'Every project')} />}
                </Field>
              )}

              {isCountyProvider && (
                <Field label="Counties served">
                  {isEdit
                    ? <>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontFamily: '"DM Sans",sans-serif', color: 'var(--aj-text)', marginBottom: 6, cursor: 'pointer' }}>
                          <input type="checkbox" checked={nationwide} disabled={servedCounties === null} onChange={e => setNationwide(e.target.checked)} />
                          Nationwide
                        </label>
                        <input style={S.input} value={servedCounties || ''} onChange={e => setServedCounties(e.target.value)}
                          placeholder={servedCounties === null ? 'Loading…' : 'e.g. Orange, Los Angeles'} disabled={servedCounties === null || nationwide} />
                        <div style={{ fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 4, fontFamily: '"DM Sans",sans-serif' }}>
                          County names separated by commas. Offered to clients on projects in these counties. Empty and not nationwide = not offered anywhere.
                        </div>
                      </>
                    : <ReadValue value={servedCounties === null ? '…' : (nationwide ? 'Nationwide' : (servedCounties ? servedCounties.split(', ').map(c => c + ' County').join(', ') : 'Not offered anywhere yet'))} />}
                </Field>
              )}

              {/* Org membership — save-immediately (each add/remove writes the CONTACT record's own
                  Organization Contacts field right away), independent of this modal's own Save/Cancel.
                  Hidden during create — an org needs to exist before contacts can link to it. */}
              {!isCreate && (
                <Field label="Contacts">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {orgMembers.length === 0 && !addingMember && (
                      <div style={{ ...S.value, color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>No contacts yet</div>
                    )}
                    {orgMembers.length > 0 && (
                      <div style={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {orgMembers.map(m => (
                          <div key={m.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: isEdit ? '5px 8px' : '2px 0', background: isEdit ? 'var(--aj-bg-subtle)' : 'transparent', borderRadius: 4 }}>
                            <span style={{ ...S.value, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {m.name}
                              {primaryId === m.id && (
                                <span style={{ marginLeft: 6, fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)' }}>PRIMARY</span>
                              )}
                            </span>
                            {isEdit && (
                              <button type="button" onClick={() => handleRemoveMember(m.id)} disabled={memberBusy === m.id}
                                style={{ background: 'none', border: 'none', cursor: memberBusy === m.id ? 'default' : 'pointer', color: '#c0392b', fontFamily: '"DM Sans",sans-serif', fontSize: 11, padding: 0, flexShrink: 0 }}>
                                {memberBusy === m.id ? 'Removing…' : 'Remove'}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {isEdit && (addingMember ? (
                      <ContactPicker value="" name="" seed={contacts} inputStyle={S.input} onChange={handleAddMember} />
                    ) : (
                      <button type="button" onClick={() => setAddingMember(true)}
                        style={{ alignSelf: 'flex-start', background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: 0 }}>
                        + Add contact
                      </button>
                    ))}
                  </div>
                </Field>
              )}
            </>
          )}

          {error && (
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>
          )}

          {/* Actions */}
          {isEdit && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
              <button
                onClick={onClose}
                style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}
              >Cancel</button>
              <button
                onClick={handleSave}
                disabled={submitting}
                style={{
                  padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600,
                  background: submitting ? 'var(--aj-bg-muted)' : '#07272D',
                  color: submitting ? 'var(--aj-text-muted)' : '#fff',
                  border: 'none', borderRadius: 4, cursor: submitting ? 'default' : 'pointer',
                }}
              >{submitting ? 'Saving…' : (isCreate ? 'Create' : 'Save')}</button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
